import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../config';

const cartApiUrl = `${API_URL}/Cart`;

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks for server operations

// Fetch cart from server
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(cartApiUrl, { headers: getAuthHeader() });
      const list = Array.isArray(response.data) ? response.data : (response.data.$values || []);
      return list.map((item) => ({
        ...item,
        productId: item.productId ?? item.ProductId,
        variantId: item.variantId ?? item.VariantId ?? null,
        quantity: item.quantity ?? item.Quantity,
        stock: item.stock ?? item.Stock,
        imageUrl: item.imageUrl ?? item.ImageUrl,
        colorName: item.colorName ?? item.ColorName ?? null,
        sizeName: item.sizeName ?? item.SizeName ?? null,
      }));
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add item to cart (server). Pass price/originalPrice to store selected seller's price.
const toCartId = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const sameCartLine = (item, productId, variantId) =>
  toCartId(item?.productId ?? item?.ProductId) === toCartId(productId)
  && toCartId(item?.variantId ?? item?.VariantId) === toCartId(variantId);

const getMaxStock = (item, fallback) => {
  const value = item?.stock ?? fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};

export const addToCartAsync = createAsyncThunk(
  'cart/addToCartAsync',
  async ({ productId, quantity = 1, price, originalPrice, productSellerId, variantId }, { rejectWithValue }) => {
    try {
      const body = { productId, quantity };
      if (price != null && price !== '') body.price = Number(price);
      if (originalPrice != null && originalPrice !== '') body.originalPrice = Number(originalPrice);
      if (productSellerId != null && productSellerId !== '') body.productSellerId = Number(productSellerId);
      if (variantId != null && variantId !== '') body.variantId = Number(variantId);
      const response = await axios.post(cartApiUrl, body, { headers: getAuthHeader() });
      const data = response.data || {};
      return {
        ...data,
        productId: data.productId ?? data.ProductId,
        variantId: data.variantId ?? data.VariantId ?? null,
        quantity: data.quantity ?? data.Quantity,
        stock: data.stock ?? data.Stock,
        imageUrl: data.imageUrl ?? data.ImageUrl,
        colorName: data.colorName ?? data.ColorName ?? null,
        sizeName: data.sizeName ?? data.SizeName ?? null,
      };
    } catch (error) {
      const data = error.response?.data;
      const message = typeof data === 'string'
        ? data
        : data?.message || data?.title || error.message;
      return rejectWithValue(message);
    }
  }
);

// Update cart item quantity (server)
export const updateCartItemAsync = createAsyncThunk(
  'cart/updateCartItemAsync',
  async ({ productId, quantity, variantId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${cartApiUrl}/${productId}`, { quantity, variantId: variantId ?? null }, { headers: getAuthHeader() });
      return { productId, quantity, variantId: variantId ?? null };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Remove item from cart (server)
export const removeFromCartAsync = createAsyncThunk(
  'cart/removeFromCartAsync',
  async (payload, { rejectWithValue }) => {
    const productId = typeof payload === 'object' ? payload.productId : payload;
    const variantId = typeof payload === 'object' ? payload.variantId : undefined;
    try {
      await axios.delete(`${cartApiUrl}/${productId}`, {
        headers: getAuthHeader(),
        params: variantId != null ? { variantId } : undefined,
      });
      return { productId, variantId: variantId ?? null };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Clear cart (server)
export const clearCartAsync = createAsyncThunk(
  'cart/clearCartAsync',
  async (_, { rejectWithValue }) => {
    try {
      await axios.delete(cartApiUrl, { headers: getAuthHeader() });
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Sync local cart with server on login
export const syncCartWithServer = createAsyncThunk(
  'cart/syncCartWithServer',
  async (localItems, { rejectWithValue }) => {
    try {
      const items = localItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.price != null ? item.price : undefined,
        originalPrice: item.originalPrice != null ? item.originalPrice : undefined,
        productSellerId: item.productSellerId != null ? item.productSellerId : undefined,
        variantId: item.variantId != null ? item.variantId : undefined,
      }));
      // Use 'replace' strategy to set exact quantities, not add to existing
      const response = await axios.post(`${cartApiUrl}/sync`, { items, mergeStrategy: 'replace' }, { headers: getAuthHeader() });
      const list = Array.isArray(response.data) ? response.data : (response.data.$values || []);
      return list.map((item) => ({
        ...item,
        productId: item.productId ?? item.ProductId,
        variantId: item.variantId ?? item.VariantId ?? null,
        quantity: item.quantity ?? item.Quantity,
        stock: item.stock ?? item.Stock,
        imageUrl: item.imageUrl ?? item.ImageUrl,
        colorName: item.colorName ?? item.ColorName ?? null,
        sizeName: item.sizeName ?? item.SizeName ?? null,
      }));
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: [], // Array of { productId, productName, price, imageUrl, quantity }
    status: 'idle',
    error: null,
    isLoggedIn: false, // Track if we should sync with server
  },
  reducers: {
    // Local cart operations (for non-logged-in users)
    addToCart: (state, action) => {
      const { productId, productName, price, originalPrice, imageUrl, sellerName, sellerCode, productSellerId, deliveryCharge, variantId, colorName, sizeName, stock } = action.payload;
      const existingItem = state.items.find((item) => sameCartLine(item, productId, variantId));
      const maxStock = getMaxStock(existingItem, stock);

      if (existingItem) {
        if (existingItem.quantity >= maxStock) return;
        existingItem.quantity++;
        if (stock != null) existingItem.stock = stock;
        if (sellerName != null) existingItem.sellerName = sellerName;
        if (sellerCode != null) existingItem.sellerCode = sellerCode;
        if (productSellerId != null) existingItem.productSellerId = productSellerId;
        if (deliveryCharge != null) existingItem.deliveryCharge = deliveryCharge;
      } else {
        state.items.push({
          productId, productName, price, originalPrice: originalPrice ?? null, imageUrl, quantity: 1,
          sellerName: sellerName ?? null, sellerCode: sellerCode ?? null, productSellerId: productSellerId ?? null,
          deliveryCharge: deliveryCharge ?? 0,
          variantId: variantId ?? null, colorName: colorName ?? null, sizeName: sizeName ?? null,
          stock: stock ?? null,
        });
      }
    },
    removeFromCart: (state, action) => {
      const payload = action.payload;
      const productIdToRemove = typeof payload === 'object' ? payload.productId : payload;
      const variantId = typeof payload === 'object' ? payload.variantId : undefined;
      state.items = state.items.filter((item) => !sameCartLine(item, productIdToRemove, variantId));
    },
    updateQuantity: (state, action) => {
      const { productId, quantity, variantId, stock } = action.payload;
      const itemToUpdate = state.items.find((item) => sameCartLine(item, productId, variantId));

      if (itemToUpdate) {
        if (stock != null) itemToUpdate.stock = stock;
        const maxStock = getMaxStock(itemToUpdate);
        itemToUpdate.quantity = Math.min(quantity, maxStock);
        if (itemToUpdate.quantity <= 0) {
          state.items = state.items.filter((item) => !sameCartLine(item, productId, variantId));
        }
      }
    },
    clearCart: (state) => {
      state.items = [];
    },
    setCartLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart
      .addCase(fetchCart.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Sync cart
      .addCase(syncCartWithServer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(syncCartWithServer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
        state.isLoggedIn = true;
        state.error = null;
      })
      .addCase(syncCartWithServer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Add to cart async
      .addCase(addToCartAsync.fulfilled, (state, action) => {
        const { productId, productName, price, originalPrice, imageUrl, sellerName, sellerCode, productSellerId, deliveryCharge, variantId, colorName, sizeName, quantity, stock } = action.payload;
        const existingItem = state.items.find((item) => sameCartLine(item, productId, variantId ?? null));
        const nextQuantity = Number(quantity) > 0 ? Number(quantity) : ((existingItem?.quantity || 0) + 1);
        if (existingItem) {
          existingItem.quantity = nextQuantity;
          if (stock != null) existingItem.stock = stock;
          if (imageUrl) existingItem.imageUrl = imageUrl;
          if (sellerName != null) existingItem.sellerName = sellerName;
          if (sellerCode != null) existingItem.sellerCode = sellerCode;
          if (productSellerId != null) existingItem.productSellerId = productSellerId;
          if (deliveryCharge != null) existingItem.deliveryCharge = deliveryCharge;
          if (colorName != null) existingItem.colorName = colorName;
          if (sizeName != null) existingItem.sizeName = sizeName;
        } else {
          state.items.push({
            productId, productName, price, originalPrice: originalPrice ?? null, imageUrl, quantity: nextQuantity,
            sellerName: sellerName ?? null,
            sellerCode: sellerCode ?? null,
            productSellerId: productSellerId ?? null,
            deliveryCharge: deliveryCharge ?? 0,
            variantId: variantId ?? null,
            colorName: colorName ?? null,
            sizeName: sizeName ?? null,
            stock: stock ?? null,
          });
        }
      })
      // Update cart item
      .addCase(updateCartItemAsync.fulfilled, (state, action) => {
        const { productId, quantity, variantId } = action.payload;
        if (quantity <= 0) {
          state.items = state.items.filter((item) => !sameCartLine(item, productId, variantId ?? null));
        } else {
          const item = state.items.find((item) => sameCartLine(item, productId, variantId ?? null));
          if (item) {
            item.quantity = quantity;
          }
        }
      })
      // Remove from cart
      .addCase(removeFromCartAsync.fulfilled, (state, action) => {
        const { productId, variantId } = action.payload;
        state.items = state.items.filter((item) => !sameCartLine(item, productId, variantId ?? null));
      })
      // Clear cart
      .addCase(clearCartAsync.fulfilled, (state) => {
        state.items = [];
      });
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  setCartLoggedIn,
} = cartSlice.actions;

export default cartSlice.reducer;
